/**
 * AP-46 — Baustellendokumente (`SITE_DOCS`): der Vertrag zwischen Gerät, ERP und Server.
 *
 * Hier steht, was alle drei gleich wissen müssen: Arten, Zustände, die Vorgaben der
 * Einstellungen, der **Kopf des kanonischen Stands** und die Form der Übertragung vom
 * Gerät. Der Kopf wird auf dem Gerät und im Server mit **derselben** Funktion gebaut
 * (`siteDocKanonischerStand`) — der Server rechnet aus den übermittelten Feldern denselben
 * Text nach und vergleicht Zeichen für Zeichen (AP-46 6., Schritt 5).
 *
 * Spezifikation: docs/Arbeitspakete/AP-46/AP-46_Baustellendokumente.md
 */
import { datumText, kanonischesJson, inhaltsHash, zeitpunktText } from './kanonisch'
import { formatiereBelegnummer } from './documents-fields'

export const SITE_DOC_KINDS = ['REGIE', 'ASSEMBLY', 'DIARY', 'MEETING'] as const
export type SiteDocKind = (typeof SITE_DOC_KINDS)[number]

export const SITE_DOC_STATUSES = ['DRAFT', 'AWAITING', 'FINAL', 'DECLINED', 'VOIDED', 'DISCARDED'] as const
export type SiteDocStatus = (typeof SITE_DOC_STATUSES)[number]

export const SITE_DOC_RELEASES = ['SIGNATURE', 'LINK', 'INTERNAL'] as const
export type SiteDocRelease = (typeof SITE_DOC_RELEASES)[number]

export const SITE_DOC_SIGNER_ROLES = ['CUSTOMER', 'CONTRACTOR', 'SUPERVISOR', 'OTHER'] as const
export type SiteDocSignerRole = (typeof SITE_DOC_SIGNER_ROLES)[number]

export const SITE_DOC_SIGNATURE_CHANNELS = ['DEVICE', 'ERP', 'LINK'] as const
export type SiteDocSignatureChannel = (typeof SITE_DOC_SIGNATURE_CHANNELS)[number]

export const SITE_DOC_FILE_ROLES = ['PHOTO', 'SIGNATURE', 'PDF'] as const
export type SiteDocFileRole = (typeof SITE_DOC_FILE_ROLES)[number]

export const SITE_DOC_PROJECT_SOURCES = ['PROJECT', 'TIME_REF', 'TEXT'] as const
export type SiteDocProjectSource = (typeof SITE_DOC_PROJECT_SOURCES)[number]

/** Zustände, in denen der Inhalt eingefroren ist und einen Hash trägt. */
export const SITE_DOC_FROZEN: readonly SiteDocStatus[] = ['AWAITING', 'FINAL', 'DECLINED', 'VOIDED']

/** Baustein je Art (4.1). */
export const SITE_DOC_KIND_MODULE = {
  REGIE: 'SITE_DOCS.REGIE',
  ASSEMBLY: 'SITE_DOCS.ASSEMBLY',
  DIARY: 'SITE_DOCS.DIARY',
  MEETING: 'SITE_DOCS.MEETING',
} as const satisfies Record<SiteDocKind, string>

/** Anlegerecht je Art (4.3). */
export const SITE_DOC_KIND_PERMISSION = {
  REGIE: 'sitedocs.regie.create',
  ASSEMBLY: 'sitedocs.assembly.create',
  DIARY: 'sitedocs.diary.create',
  MEETING: 'sitedocs.meeting.create',
} as const satisfies Record<SiteDocKind, string>

/** Pfadteil je Art — `/site-docs/<art>` im Backend, `/site-docs/new/<art>` im ERP. */
export const SITE_DOC_KIND_PATH = {
  REGIE: 'regie',
  ASSEMBLY: 'assembly',
  DIARY: 'diary',
  MEETING: 'meeting',
} as const satisfies Record<SiteDocKind, string>

// ── Nummern (7.) ─────────────────────────────────────────────────────────────

/** Vorgabe des Musters: `RB-2026-0014`. */
export const SITE_DOC_DEFAULT_PATTERN = '{prefix}{jahr}-{nummer}'
export const SITE_DOC_DEFAULT_DIGITS = 4
export const SITE_DOC_DEFAULT_BLOCK_SIZE = 20
/** Unter so vielen freien Nummern holt das Gerät einen neuen Block bzw. warnt (7.). */
export const SITE_DOC_BLOCK_LOW = 5

export const SITE_DOC_DEFAULT_PREFIX = {
  REGIE: 'RB-',
  ASSEMBLY: 'MP-',
  DIARY: 'BT-',
  MEETING: 'BP-',
} as const satisfies Record<SiteDocKind, string>

export function siteDocNumber(
  einstellung: { prefix: string; numberPattern: string; digits: number },
  jahr: number,
  laufnummer: number,
): string {
  return formatiereBelegnummer(einstellung.numberPattern, {
    prefix: einstellung.prefix,
    jahr,
    laufnummer,
    stellen: einstellung.digits,
  })
}

/**
 * Welche Jahre ein Gerät mit Nummern versorgt hält (7.): das laufende; ab dem
 * 1. Dezember zusätzlich das Folgejahr; bis Ende Jänner noch das Vorjahr.
 */
export function siteDocBlockYears(heute: Date): number[] {
  const jahr = heute.getUTCFullYear()
  const monat = heute.getUTCMonth() + 1
  const jahre = [jahr]
  if (monat === 12) jahre.push(jahr + 1)
  if (monat === 1) jahre.unshift(jahr - 1)
  return jahre
}

// ── Einstellungen je Art (11.2) ─────────────────────────────────────────────

export interface SiteDocKindSettings {
  kind: SiteDocKind
  prefix: string
  numberPattern: string
  digits: number
  blockSize: number
  confirmationText: string | null
  minPhotos: number
  maxPhotos: number
  requiredSigners: SiteDocSignerRole[]
  allowedReleases: SiteDocRelease[]
  linkValidDays: number
  linkReminderDays: number[]
  /** Einstellungen der Art (beim Regiebericht etwa die Zuschlagsliste). */
  extra: Record<string, unknown> | null
  updatedAt?: string
}

/**
 * RN-1 (AP-47 15.) — **Freigabe nach Inhalt.** Was für ein bestimmtes Dokument gilt: welche
 * Freigaben erlaubt sind, wer unterschreiben muss und welcher Bestätigungstext darüber steht. Ohne
 * Regel der Art ist es die Einstellung; mit Regel die Schnittmenge (`siteDocFreigabeSchnitt`).
 */
export interface SiteDocFreigabe {
  allowedReleases: SiteDocRelease[]
  requiredSigners: SiteDocSignerRole[]
  confirmationText: string | null
}

/**
 * Die Schnittmenge aus Einstellung des Mandanten und Regel der Art: Was der Mandant für die Art
 * abgeschaltet hat, schaltet der Inhalt nicht wieder ein. Pflicht-Unterzeichner und
 * Bestätigungstext bestimmt die Regel (sie bekommt die Einstellung und entscheidet selbst).
 * Ohne Regel gilt die Einstellung unverändert.
 */
export function siteDocFreigabeSchnitt(
  einstellung: { allowedReleases: readonly SiteDocRelease[]; requiredSigners: readonly SiteDocSignerRole[]; confirmationText: string | null },
  regel: SiteDocFreigabe | null | undefined,
): SiteDocFreigabe {
  if (!regel) return { allowedReleases: [...einstellung.allowedReleases], requiredSigners: [...einstellung.requiredSigners], confirmationText: einstellung.confirmationText }
  return {
    allowedReleases: SITE_DOC_RELEASES.filter((r) => einstellung.allowedReleases.includes(r) && regel.allowedReleases.includes(r)),
    requiredSigners: [...regel.requiredSigners],
    confirmationText: regel.confirmationText,
  }
}

/** Was das Gerät über eine Art wissen darf — nicht die Admin-Einstellungen (13., `device/config`). */
export interface SiteDocDeviceKindConfig {
  kind: SiteDocKind
  prefix: string
  numberPattern: string
  digits: number
  confirmationText: string | null
  minPhotos: number
  maxPhotos: number
  requiredSigners: SiteDocSignerRole[]
  allowedReleases: SiteDocRelease[]
  extra: Record<string, unknown> | null
}

export interface SiteDocDeviceConfig {
  /** Nur Arten, deren Baustein gebucht ist und die der Benutzer anlegen darf. */
  kinds: SiteDocDeviceKindConfig[]
  /** Woher die Projektauswahl kommt (24.2). */
  projectSources: SiteDocProjectSource[]
  /** Diktat mit KI gebucht und eingeschaltet (21.7). */
  aiDictation: boolean
  /** `document.edit_others` — darf für andere übertragen (8.3). */
  canTransferForOthers: boolean
  /** Name des Betriebs — für Bestätigungsanzeige und Prüfseite. */
  tenantName: string
  /** Anfang der Adresse der Prüfseite (`…/pruefen/`) — das Gerät hängt sein Token an (9. Schritt 6, 23.3). */
  verifyUrlBase: string
  serverTime: string
}

// ── Schnappschuss und kanonischer Stand (6.) ─────────────────────────────────

/** Projekt, Kunde, Baustelle, Erfasser — als Werte zum Zeitpunkt der Erfassung. */
export interface SiteDocSnapshot {
  project: { number: string | null; name: string }
  customer: string | null
  site: string | null
  author: { id: string; name: string }
}

export function siteDocProjectLabel(s: Pick<SiteDocSnapshot, 'project'>): string {
  return [s.project.number, s.project.name].filter((t) => t && t.trim()).join(' ').trim()
}

export interface SiteDocKanonFoto {
  clientId: string
  sha256: string
  caption: string | null
}

export type SiteDocBezugArt = 'SUPPLEMENT' | 'REPLACEMENT'

export interface SiteDocKanonEingabe {
  kind: SiteDocKind
  number: string
  release: SiteDocRelease
  snapshot: SiteDocSnapshot
  documentDate: string | Date
  title: string
  /** Fachteil der Art, schon in kanonischer Form (Mengen über `festeZahl`). */
  fachteil: unknown
  /** Fassung des Fachteils der Art. */
  schemaVersion: number
  photos: SiteDocKanonFoto[]
  /** Bei `SIGNATURE` und `LINK`. */
  confirmationText: string | null
  finalizedAtDevice: string | Date
  /** Nachtrag bzw. Ersatz: Nummer und Prüfwert des ursprünglichen Dokuments. */
  bezug: { art: SiteDocBezugArt; number: string; contentHash: string } | null
}

/** Fassung des Kopfs. Eine Änderung am Aufbau unten ist eine neue Fassung. */
export const SITE_DOC_KOPF_FASSUNG = 1

/**
 * Das Objekt, über das gehasht wird. **Nicht** darin: Unterschriftsbilder (sie binden
 * sich an den Hash), Eingangszeit, PDF, Token der Prüfseite und die inneren
 * Zuordnungen — Position, Schritt, Buchungen, IDs von Artikeln und Mitarbeitern
 * (BD-24). Personennamen und Bezeichnungen stehen als Schnappschuss darin.
 */
export function siteDocKanonObjekt(e: SiteDocKanonEingabe): Record<string, unknown> {
  return {
    fassung: SITE_DOC_KOPF_FASSUNG,
    art: e.kind,
    nummer: e.number,
    freigabe: e.release,
    projekt: { nummer: e.snapshot.project.number, bezeichnung: e.snapshot.project.name },
    kunde: e.snapshot.customer,
    baustelle: e.snapshot.site,
    datum: datumText(e.documentDate),
    titel: e.title,
    schemaVersion: e.schemaVersion,
    fachteil: e.fachteil,
    fotos: e.photos.map((f) => ({ id: f.clientId, sha256: f.sha256, bildunterschrift: f.caption })),
    bestaetigungstext: e.release === 'INTERNAL' ? null : e.confirmationText,
    erfasser: { id: e.snapshot.author.id, name: e.snapshot.author.name },
    abschlusszeit: zeitpunktText(e.finalizedAtDevice),
    bezug: e.bezug ? { art: e.bezug.art, nummer: e.bezug.number, pruefwert: e.bezug.contentHash } : null,
  }
}

/** Text und Hash des eingefrorenen Stands. */
export async function siteDocEinfrieren(e: SiteDocKanonEingabe): Promise<{ text: string; hash: string }> {
  const text = kanonischesJson(siteDocKanonObjekt(e))
  return { text, hash: await inhaltsHash(text) }
}

/**
 * Der Bestätigungstext mit Datum und Projekt (AP-27 2., Vorgabe je Art). Das Datum kommt als
 * Kalendertag (`JJJJ-MM-TT` oder `Date`) und steht im Text als `TT.MM.JJJJ` — Gerät, ERP und Server
 * setzen ihn mit dieser einen Funktion zusammen, sonst unterschiede sich der eingefrorene Text je Weg.
 */
export function siteDocBestaetigungstext(vorlage: string | null, datum: string | Date, projekt: string): string | null {
  if (!vorlage) return null
  const tag = datumText(datum).split('-').reverse().join('.')
  return vorlage.split('{Datum}').join(tag).split('{Projekt}').join(projekt)
}

// ── Übertragung vom Gerät (8.2) ─────────────────────────────────────────────

export interface SiteDocTransferFile {
  clientId: string
  role: Extract<SiteDocFileRole, 'PHOTO' | 'SIGNATURE'>
  sha256: string
  sizeBytes: number
  mimeType: string
  caption: string | null
  takenAt: string | null
  sortOrder: number
}

export interface SiteDocTransferSignature {
  clientId: string
  role: SiteDocSignerRole
  name: string
  function: string | null
  email: string | null
  contactId: string | null
  userId: string | null
  imageFileClientId: string
  signedAtDevice: string
}

export interface SiteDocTransferProject {
  source: SiteDocProjectSource
  projectId: string | null
  projectRefId: string | null
  customerId: string | null
  positionId: string | null
  workStepId: string | null
}

export interface SiteDocTransferDocument {
  clientId: string
  kind: SiteDocKind
  /** `DRAFT` = „Ins Büro übergeben"; sonst ein eingefrorener Stand. */
  status: Extract<SiteDocStatus, 'DRAFT' | 'AWAITING' | 'FINAL' | 'DECLINED'>
  /** Erfasser (für „Für {Name} übertragen" ein anderer als der angemeldete Benutzer). */
  createdById: string
  deviceId: string
  project: SiteDocTransferProject
  snapshot: SiteDocSnapshot
  documentDate: string
  title: string
  /** Strukturierter Fachinhalt der Art — aus ihm baut der Server den Fachteil nach. */
  content: Record<string, unknown> | null
  dictation: string | null
  number: string | null
  numberYear: number | null
  numberSeq: number | null
  numberBlockId: string | null
  release: SiteDocRelease | null
  confirmationText: string | null
  canonicalJson: string | null
  contentHash: string | null
  schemaVersion: number | null
  finalizedAtDevice: string | null
  declineReason: string | null
  /** Token der Prüfseite im Klartext — der Server speichert nur den Hash (23.3). */
  publicToken: string | null
  supplementsId: string | null
  supersedesId: string | null
  /** Adresse für die Kopie (aus der Unterschrift). */
  copyEmail: string | null
  /** Bei `AWAITING`: an wen der Link geht (22.). */
  confirmationRequest: { email: string; name: string | null; contactId: string | null } | null
  files: SiteDocTransferFile[]
  signatures: SiteDocTransferSignature[]
  /** Geräteuhr beim Absenden. */
  deviceTime: string
  capturedOffline: boolean
}

/** Stand einer Übertragung je `clientId` (13., `device/state`). */
export type SiteDocTransferState =
  | 'UNKNOWN'
  | 'FILES_MISSING'
  | 'SAFE'
  | 'QUARANTINE'

export interface SiteDocTransferResult {
  clientId: string
  state: SiteDocTransferState
  documentId: string | null
  /** clientIds der Dateien, die dem Server noch fehlen. */
  missingFiles: string[]
}

export interface SiteDocHeartbeatItem {
  clientId: string
  number: string | null
  kind: SiteDocKind
  projectId: string | null
  projectRefId: string | null
  projectLabel: string
  documentDate: string
  status: SiteDocStatus
}

export interface SiteDocNumberBlockView {
  id: string
  kind: SiteDocKind
  year: number
  firstValue: number
  lastValue: number
  requestId: string
  withdrawn: boolean
}

// ── Vorzeige im ERP (9.) ─────────────────────────────────────────────────────

/**
 * Was das ERP vor der Unterschrift zeigt: der kanonische Stand, genau wie er eingefroren wird
 * (`siteDocKanonObjekt`, Abschlusszeit = jetzt), dazu die Kontakte für den Unterzeichner.
 */
export interface SiteDocPreview {
  objekt: Record<string, unknown>
  release: SiteDocRelease
  requiredSigners: SiteDocSignerRole[]
  contacts: SiteDocProjectContact[]
}

// ── Bestätigung per Link (22.) und Prüfseite (23.) ──────────────────────────

/** Empfänger der Bestätigung per Link — aus den Kontakten oder eingetragen (22.1). */
export interface SiteDocLinkRecipient {
  email: string
  name?: string | null
  contactId?: string | null
}

/** Stand der Bestätigung per Link am Detail (22.2) — die jüngste, nicht zurückgenommene. */
export interface SiteDocConfirmationView {
  email: string
  name: string | null
  sentAt: string | null
  expiresAt: string
  openedAt: string | null
  remindedAt: string[]
  answeredAt: string | null
  expired: boolean
}

/** `GET /site-docs/public/confirm/:token` — was der Kunde im eigenen Browser sieht (22.1 Nr. 3). */
export interface SiteDocPublicConfirm {
  /** `OPEN` bestätigbar · `EXPIRED` abgelaufen · `ANSWERED` schon beantwortet · `UNKNOWN` unbekannt oder zurückgenommen */
  state: 'OPEN' | 'EXPIRED' | 'ANSWERED' | 'UNKNOWN'
  tenantName: string | null
  kind: SiteDocKind | null
  number: string | null
  documentDate: string | null
  /** Das kanonische Objekt — gezeichnet wie die Vorzeigeansicht. */
  objekt: Record<string, unknown> | null
  photos: { id: string; caption: string | null }[]
  expiresAt: string | null
  /** nur bei `ANSWERED` */
  answer: 'CONFIRMED' | 'DECLINED' | null
  name: string | null
}

/** `GET /site-docs/public/check/:token` (23.2) — nicht mehr, als im PDF steht. */
export interface SiteDocPublicCheck {
  found: boolean
  /** Das Dokument liegt noch auf dem Gerät (Token aus dem Gerät, beim Server unbekannt). */
  arrived: boolean
  tenantName: string | null
  kind: SiteDocKind | null
  number: string | null
  documentDate: string | null
  status: SiteDocStatus | null
  contentHash: string | null
  /** bei Storno: Nummer des Ersatzes */
  supersededBy: string | null
  /** Nummern der Nachträge */
  supplements: string[]
  pdf: boolean
}

/** Bezug eines Nachtrags oder Ersatzes — Nummer und Prüfwert des ursprünglichen Dokuments (5.3). */
export interface SiteDocReference {
  art: SiteDocBezugArt
  number: string
  contentHash: string
}

// ── Projektauswahl (24.2) ───────────────────────────────────────────────────

export interface SiteDocProjectContact {
  id: string | null
  name: string
  function: string | null
  email: string | null
  /** AP-49 RN-9 — Firma: beim Beteiligten seine Angabe, beim Ansprechpartner der Kunde. Optional: ältere Zwischenspeicher der Geräte kennen sie nicht. */
  company?: string | null
}

export interface SiteDocProjectChoice {
  source: Extract<SiteDocProjectSource, 'PROJECT' | 'TIME_REF'>
  id: string
  number: string | null
  name: string
  customerId: string | null
  customerName: string | null
  siteAddress: string | null
  contacts: SiteDocProjectContact[]
  /** Warum das Projekt in der Auswahl steht. */
  reason: 'ASSIGNED' | 'BOOKED' | 'TIME_PROJECT' | 'SEARCH'
}

// ── Akte (10.4, 11.2) ───────────────────────────────────────────────────────

export interface SiteDocListRow {
  id: string
  kind: SiteDocKind
  status: SiteDocStatus
  number: string | null
  documentDate: string
  title: string
  projectLabel: string
  projectSource: SiteDocProjectSource
  projectId: string | null
  projectRefId: string | null
  createdById: string
  createdByName: string
  release: SiteDocRelease | null
  filesComplete: boolean
  copyEmail: string | null
  copySentAt: string | null
  heldByUserId: string | null
  signedBy: string | null
  receivedAt: string | null
  fetchedAt: string | null
  updatedAt: string
  /** Zeile aus dem Herzschlag: liegt noch auf einem Gerät, ist beim Server nicht angekommen. */
  onDevice?: boolean
  /** Werte der Art für eigene Spalten der Akte (Regiebericht: Stunden, Zeilen, abgerechnet). */
  kindValues?: Record<string, unknown>
}

export interface SiteDocSignatureView {
  id: string
  role: SiteDocSignerRole
  channel: SiteDocSignatureChannel
  name: string
  function: string | null
  email: string | null
  userId: string | null
  contentHash: string
  imageFileId: string | null
  signedAtDevice: string
  deviceId: string | null
  capturedById: string | null
  capturedOffline: boolean
  userAgent: string | null
  ipAddress: string | null
  linkEmail: string | null
  receivedAt: string
}

export interface SiteDocFileView {
  id: string
  clientId: string
  role: SiteDocFileRole
  present: boolean
  sha256: string
  sizeBytes: number
  mimeType: string | null
  caption: string | null
  takenAt: string | null
  sortOrder: number
  lostAt: string | null
  lostReason: string | null
  createdAt: string
}

export interface SiteDocChainLink {
  id: string
  number: string | null
  status: SiteDocStatus
  relation: 'SUPPLEMENTS' | 'SUPPLEMENTED_BY' | 'SUPERSEDES' | 'SUPERSEDED_BY'
}

export interface SiteDocDetail extends SiteDocListRow {
  snapshot: SiteDocSnapshot
  content: Record<string, unknown> | null
  dictation: string | null
  customerId: string | null
  positionId: string | null
  workStepId: string | null
  projectOutsideSelection: boolean
  deviceId: string | null
  capturedOffline: boolean
  heldSince: string | null
  clockSkewSeconds: number | null
  confirmationText: string | null
  canonicalJson: string | null
  contentHash: string | null
  schemaVersion: number | null
  finalizedAtDevice: string | null
  finalizedById: string | null
  followUpDoneAt: string | null
  followUpError: string | null
  declineReason: string | null
  pdfHash: string | null
  supersedesId: string | null
  supplementsId: string | null
  voidedAt: string | null
  voidReason: string | null
  discardedAt: string | null
  discardReason: string | null
  /** Adresse der Prüfseite — für den QR-Code der Bestätigungsanzeige im ERP (9. Schritt 6, 23.). */
  verifyUrl: string | null
  /** AP-46 I — Bestätigung per Link: die geltende Anfrage (sonst `null`). */
  confirmation: SiteDocConfirmationView | null
  /** Freigaben, die die Art beim Mandanten erlaubt (Einstellungen, 11.2). */
  allowedReleases: SiteDocRelease[]
  signatures: SiteDocSignatureView[]
  files: SiteDocFileView[]
  chain: SiteDocChainLink[]
  /** Was der angemeldete Benutzer an diesem Dokument tun darf. */
  can: {
    edit: boolean
    present: boolean
    finalize: boolean
    discard: boolean
    void: boolean
    send: boolean
    assign: boolean
    handToDevice: boolean
    countersign: boolean
    supplement: boolean
    /** AP-46 21.: „Vorschlag aus dem Text" — Entwurf bearbeitbar, `.AI` gebucht und eingeschaltet. */
    dictate: boolean
  }
  /** Was die Art gegen ein Storno meldet (5.3). */
  voidBlockers: string[]
  /** Was vor dem Abschluss fehlt (`pruefe` der Art). */
  missing: string[]
}

// ── Gegenzeichnung auf dem Gerät (AP-48 RN-5) und per Sammel-Link (RN-6) ────

/** Höchstens so viele Dokumente je Sammel-Anfrage und je „Zur Gegenzeichnung holen" (AP-48 TB-9, TB-11). */
export const SITE_DOC_COUNTERSIGN_MAX = 31

/**
 * Eine Gegenzeichnung, die auf dem Gerät entstanden ist (RN-5) — an einem dort freigegebenen Dokument
 * (`documentClientId`) oder an einem geholten (`documentId`). Gebunden an den Prüfwert, den das Gerät
 * kennt; der Server prüft ihn beim Eintreffen wie bei jeder Unterschrift. Idempotent über `clientId`.
 */
export interface SiteDocDeviceCountersignature {
  clientId: string
  documentId: string | null
  documentClientId: string | null
  contentHash: string
  role: SiteDocSignerRole
  name: string
  function: string | null
  email: string | null
  contactId: string | null
  signedAtDevice: string
  /** SHA-256 des PNG, das mitkommt */
  imageSha256: string
  deviceId: string
  capturedOffline: boolean
}

/** `GET /site-docs/device/countersign-candidates` — ein Dokument, lesend aufs Gerät geholt (RN-5). */
export interface SiteDocCountersignCandidate {
  id: string
  clientId: string
  kind: SiteDocKind
  /** `FINAL` zum Gegenzeichnen, `AWAITING` für „Vor Ort nachholen" durch den Kunden */
  status: Extract<SiteDocStatus, 'FINAL' | 'AWAITING'>
  number: string | null
  documentDate: string
  title: string
  projectLabel: string
  contentHash: string
  /** Das kanonische Objekt — gezeichnet wie die Vorzeigeansicht. */
  objekt: Record<string, unknown> | null
  photos: { id: string; sha256: string; caption: string | null; mimeType: string | null }[]
  signatures: { role: SiteDocSignerRole; channel: SiteDocSignatureChannel; name: string }[]
  contacts: SiteDocProjectContact[]
}

/** Empfänger einer Sammel-Anfrage — aus den Kontakten des Projekts oder frei (AP-48 6.3). */
export interface SiteDocCountersignRecipient {
  email: string
  name?: string | null
  function?: string | null
  contactId?: string | null
  role: SiteDocSignerRole
}

export interface SiteDocCountersignItemView {
  documentId: string
  number: string | null
  documentDate: string
  outcome: 'SIGNED' | 'OBJECTED' | null
  objection: string | null
  answeredName: string | null
  answeredAt: string | null
}

/** Eine Sammel-Anfrage im ERP (Detail, Bautagebuch). */
export interface SiteDocCountersignRequestView {
  id: string
  email: string
  name: string | null
  function: string | null
  role: SiteDocSignerRole
  sentAt: string | null
  expiresAt: string
  openedAt: string | null
  revokedAt: string | null
  remindedAt: string[]
  expired: boolean
  /** alle Dokumente beantwortet */
  done: boolean
  createdByName: string
  items: SiteDocCountersignItemView[]
}

/** Ein Einwand der Bauaufsicht (AP-48 6.4) — kein Inhalt, steht neben dem Bericht. */
export interface SiteDocObjectionView {
  requestId: string
  name: string | null
  email: string
  text: string
  answeredAt: string
}

/** Ein Dokument auf der öffentlichen Seite der Sammel-Gegenzeichnung. */
export interface SiteDocPublicCountersignItem {
  id: string
  kind: SiteDocKind
  number: string | null
  documentDate: string
  title: string
  projectLabel: string
  /** `VOIDED`: inzwischen storniert — nicht mehr zeichenbar */
  state: 'OPEN' | 'SIGNED' | 'OBJECTED' | 'VOIDED'
  opened: boolean
  objekt: Record<string, unknown> | null
  photos: { id: string; caption: string | null }[]
  objection: string | null
}

/** `GET /site-docs/public/countersign/:token` */
export interface SiteDocPublicCountersign {
  /** `OPEN` offen (auch teilweise beantwortet) · `DONE` alles beantwortet · `EXPIRED` · `UNKNOWN` unbekannt oder zurückgezogen */
  state: 'OPEN' | 'DONE' | 'EXPIRED' | 'UNKNOWN'
  tenantName: string | null
  name: string | null
  function: string | null
  role: SiteDocSignerRole | null
  expiresAt: string | null
  projects: string[]
  items: SiteDocPublicCountersignItem[]
}
